import React, { useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Tooltip,
    IconButton,
    Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PublishIcon from '@mui/icons-material/Publish';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useTranslation } from 'react-i18next';

dayjs.extend(customParseFormat);

/* ─── Hebrew column labels (used as Excel header row) ────────────── */
/* Required columns are suffixed with "* (חובה)" so users can tell at a
   glance which cells they must fill in. The "type" / "priceIncludingVAT"
   labels also include their allowed values in parentheses. */
const REQUIRED_SUFFIX = ' * (חובה)';
const HE_COLUMNS = {
    caseId: 'מזהה תיק' + REQUIRED_SUFFIX,
    type: 'סוג תשלום (כרטיס אשראי / צ\'ק / העברה בנקאית / מזומן)' + REQUIRED_SUFFIX,
    date: 'תאריך' + REQUIRED_SUFFIX,
    amount: 'סכום' + REQUIRED_SUFFIX,
    description: 'תיאור' + REQUIRED_SUFFIX,
    reference: 'אסמכתא',
    priceIncludingVAT: 'מחיר כולל מע"מ (כן / לא)',
    bankName: 'שם הבנק',
    bankCode: 'קוד הבנק',
    bankBranchCode: 'קוד סניף בנק',
    supplierAccountNumber: 'מספר חשבון ספק',
};

/* Short labels for compact UI contexts (preview table header). */
const HE_COLUMNS_SHORT = {
    caseId: 'מזהה תיק *',
    type: 'סוג תשלום *',
    date: 'תאריך *',
    amount: 'סכום *',
    description: 'תיאור *',
    priceIncludingVAT: 'מחיר כולל מע"מ',
};

/* Payment type code mapping (matches PaymentTypeOptions on backend / UI) */
const PAYMENT_TYPE_CODES = {
    // Hebrew -> numeric code
    "כרטיס אשראי": 0,
    "צ'ק": 1,
    'צק': 1,
    "העברה בנקאית": 2,
    'מזומן': 3,
    // English fallbacks (case-insensitive handled at lookup)
    creditcard: 0,
    check: 1,
    banktransfer: 2,
    cash: 3,
    // Numeric strings
    0: 0,
    1: 1,
    2: 2,
    3: 3,
};

const BOOL_TRUE = new Set(['כן', 'yes', 'true', '1', 'y', 't']);
const BOOL_FALSE = new Set(['לא', 'no', 'false', '0', 'n', 'f']);

const parseBoolCell = (v, defaultVal = true) => {
    if (v === undefined || v === null || v === '') return defaultVal;
    const s = String(v).trim().toLowerCase();
    if (BOOL_TRUE.has(s)) return true;
    if (BOOL_FALSE.has(s)) return false;
    return defaultVal;
};

const parsePaymentType = (v) => {
    if (v === undefined || v === null || v === '') return null;
    if (typeof v === 'number') {
        return [0, 1, 2, 3].includes(v) ? v : null;
    }
    const s = String(v).trim();
    if (PAYMENT_TYPE_CODES[s] !== undefined) return PAYMENT_TYPE_CODES[s];
    const sLower = s.toLowerCase().replace(/\s+/g, '');
    if (PAYMENT_TYPE_CODES[sLower] !== undefined) return PAYMENT_TYPE_CODES[sLower];
    return null;
};

/**
 * Parse a date cell from Excel. Handles:
 * - Excel serial numbers
 * - JS Date objects (from XLSX cellDates:true)
 * - Strings like DD/MM/YYYY, YYYY-MM-DD, DD-MM-YYYY, etc.
 */
const parseDateCell = (v) => {
    if (v === undefined || v === null || v === '') return null;
    if (v instanceof Date && !Number.isNaN(v.getTime())) return dayjs(v);
    if (typeof v === 'number') {
        // Excel serial date
        const epoch = new Date(Date.UTC(1899, 11, 30));
        const ms = v * 24 * 60 * 60 * 1000;
        return dayjs(new Date(epoch.getTime() + ms));
    }
    const s = String(v).trim();
    // Try DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY first (Israel / Hebrew convention)
    const formats = ['DD/MM/YYYY', 'D/M/YYYY', 'DD-MM-YYYY', 'D-M-YYYY', 'DD.MM.YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD'];
    for (const f of formats) {
        const d = dayjs(s, f, true);
        if (d.isValid()) return d;
    }
    const fallback = dayjs(s);
    return fallback.isValid() ? fallback : null;
};

/* ─── Template builder (xlsx) ───────────────────────────────────── */
const buildTemplateWorkbook = (sampleRows = []) => {
    // Column order (RTL in Excel is handled by the viewer; we just keep logical order)
    const headerOrder = [
        HE_COLUMNS.caseId,
        HE_COLUMNS.type,
        HE_COLUMNS.date,
        HE_COLUMNS.amount,
        HE_COLUMNS.description,
        HE_COLUMNS.reference,
        HE_COLUMNS.priceIncludingVAT,
        HE_COLUMNS.bankName,
        HE_COLUMNS.bankCode,
        HE_COLUMNS.bankBranchCode,
        HE_COLUMNS.supplierAccountNumber,
    ];

    const rows = [headerOrder, ...sampleRows];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    // Set column widths for readability (clamped so headers with the
    // allowed-values hint don't produce excessively wide columns).
    ws['!cols'] = headerOrder.map((h) => ({ wch: Math.min(42, Math.max(14, h.length + 2)) }));
    // Right-to-left sheet view
    ws['!views'] = [{ RTL: true }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'תשלומים');

    // Second sheet with notes / allowed values
    const notesHeader = ['שדה', 'הערות / ערכים מותרים'];
    const notes = [
        notesHeader,
        [HE_COLUMNS.caseId, 'חובה - מזהה התיק של הלקוח שאליו ישויך התשלום'],
        [HE_COLUMNS.type, 'חובה - אחד מהבאים: כרטיס אשראי / צ\'ק / העברה בנקאית / מזומן'],
        [HE_COLUMNS.date, 'תאריך - פורמט מועדף: DD/MM/YYYY'],
        [HE_COLUMNS.amount, 'חובה - מספר חיובי (ניתן להכניס עם נקודה עשרונית)'],
        [HE_COLUMNS.description, 'חובה - תיאור התשלום'],
        [HE_COLUMNS.reference, 'אסמכתא / מספר עסקה (לא חובה)'],
        [HE_COLUMNS.priceIncludingVAT, 'כן / לא (ברירת מחדל: כן)'],
        [HE_COLUMNS.bankName, 'רק עבור תשלום מסוג צ\'ק'],
        [HE_COLUMNS.bankCode, 'רק עבור תשלום מסוג צ\'ק'],
        [HE_COLUMNS.bankBranchCode, 'רק עבור תשלום מסוג צ\'ק'],
        [HE_COLUMNS.supplierAccountNumber, 'רק עבור תשלום מסוג צ\'ק'],
    ];
    const wsNotes = XLSX.utils.aoa_to_sheet(notes);
    wsNotes['!cols'] = [{ wch: 22 }, { wch: 80 }];
    wsNotes['!views'] = [{ RTL: true }];
    XLSX.utils.book_append_sheet(wb, wsNotes, 'הוראות');

    return wb;
};

const PaymentsExcelImport = ({
    clientOptions = [],
    leadingBusiness = false,
    defaultCaseId = '',
    onImport,
    isImporting = false,
}) => {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [fileName, setFileName] = useState('');
    const [parsedRows, setParsedRows] = useState([]); // [{ __row, caseId, type, date, amount, description, ..., errors: [] }]
    const [parseError, setParseError] = useState('');

    /* caseId -> client name lookup */
    const caseIdToName = useMemo(() => {
        const m = new Map();
        clientOptions.forEach((c) => m.set(String(c.value), c.label));
        return m;
    }, [clientOptions]);

    /* Set of caseIds that actually belong to the logged-in accountant.
       - Leading business: all sub-clients from clientOptions.
       - Non-leading: just the current open client (defaultCaseId).
       Anything else must be rejected as "not under this accountant". */
    const allowedCaseIds = useMemo(() => {
        const s = new Set(clientOptions.map((c) => String(c.value)));
        if (defaultCaseId) s.add(String(defaultCaseId));
        return s;
    }, [clientOptions, defaultCaseId]);

    const validRows = useMemo(() => parsedRows.filter((r) => r.errors.length === 0), [parsedRows]);
    const invalidRowsCount = parsedRows.length - validRows.length;

    /* ─── Download template ───────────────────────────────────── */
    const handleDownloadTemplate = () => {
        // Provide one illustrative sample row to help users fill correctly
        const sample = [[
            defaultCaseId || (clientOptions[0]?.value ?? ''),
            'העברה בנקאית',
            dayjs().format('DD/MM/YYYY'),
            1000,
            'תשלום לדוגמה',
            '',
            'כן',
            '',
            '',
            '',
            '',
        ]];
        const wb = buildTemplateWorkbook(sample);
        XLSX.writeFile(wb, 'Payments_Template.xlsx');
    };

    /* ─── Export preview rows as template (already-filled) ───── */
    const handleExportCurrent = () => {
        const dataRows = parsedRows.map((r) => [
            r.caseId ?? '',
            r.typeLabel ?? '',
            r.date ? dayjs(r.date).format('DD/MM/YYYY') : '',
            r.amount ?? '',
            r.description ?? '',
            r.reference ?? '',
            r.priceIncludingVAT ? 'כן' : 'לא',
            r.bankName ?? '',
            r.bankCode ?? '',
            r.bankBranchCode ?? '',
            r.supplierAccountNumber ?? '',
        ]);
        const wb = buildTemplateWorkbook(dataRows);
        XLSX.writeFile(wb, 'Payments_Export.xlsx');
    };

    /* ─── Parse uploaded file ────────────────────────────────── */
    const handleFileChange = async (e) => {
        setParseError('');
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        try {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(buf, { type: 'array', cellDates: true });
            const ws = wb.Sheets[wb.SheetNames[0]];
            if (!ws) {
                setParseError(t('FailedToReadFile') || 'Failed to read file');
                setParsedRows([]);
                return;
            }

            // Read with header:1 so we can find our Hebrew header row manually
            const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false, blankrows: false });
            if (!aoa || aoa.length === 0) {
                setParsedRows([]);
                return;
            }

            // Build header -> index map from the first row.
            // Normalize headers so we tolerate any of:
            //   "סוג תשלום"
            //   "סוג תשלום *"
            //   "סוג תשלום * (חובה)"
            //   "סוג תשלום (כרטיס אשראי / צ'ק / …) * (חובה)"
            // All should be treated as the same column.
            const headerRow = aoa[0].map((h) => String(h || '').trim());
            const normalizeHeader = (s) =>
                String(s || '')
                    .replace(/\(.*?\)/g, ' ')     // drop parenthetical hints / "חובה"
                    .replace(/[*״"']/g, ' ')      // drop asterisks & stray quotes
                    .replace(/\s+/g, ' ')
                    .trim();
            const idx = (label) => {
                const norm = normalizeHeader(label);
                return headerRow.findIndex((h) => {
                    if (!h) return false;
                    if (h === label) return true;
                    return normalizeHeader(h) === norm;
                });
            };

            const col = {
                caseId: idx(HE_COLUMNS.caseId),
                type: idx(HE_COLUMNS.type),
                date: idx(HE_COLUMNS.date),
                amount: idx(HE_COLUMNS.amount),
                description: idx(HE_COLUMNS.description),
                reference: idx(HE_COLUMNS.reference),
                priceIncludingVAT: idx(HE_COLUMNS.priceIncludingVAT),
                bankName: idx(HE_COLUMNS.bankName),
                bankCode: idx(HE_COLUMNS.bankCode),
                bankBranchCode: idx(HE_COLUMNS.bankBranchCode),
                supplierAccountNumber: idx(HE_COLUMNS.supplierAccountNumber),
            };

            if (col.caseId === -1) {
                setParseError(`${t('CaseId')} - ${t('ColumnNotFound') || 'column not found'}: "${HE_COLUMNS.caseId}"`);
                setParsedRows([]);
                return;
            }

            const dataRows = aoa.slice(1);
            const rows = [];
            dataRows.forEach((raw, i) => {
                // Skip truly empty lines
                const hasAny = raw.some((c) => String(c ?? '').trim() !== '');
                if (!hasAny) return;

                const caseIdRaw = col.caseId >= 0 ? raw[col.caseId] : '';
                const caseId = caseIdRaw !== undefined && caseIdRaw !== null ? String(caseIdRaw).trim() : '';

                const typeRaw = col.type >= 0 ? raw[col.type] : '';
                const type = parsePaymentType(typeRaw);

                const dateDay = col.date >= 0 ? parseDateCell(raw[col.date]) : null;
                const amountRaw = col.amount >= 0 ? raw[col.amount] : '';
                const amount = amountRaw === '' || amountRaw === null || amountRaw === undefined
                    ? NaN
                    : Number(String(amountRaw).replace(/,/g, ''));

                const description = col.description >= 0 ? String(raw[col.description] ?? '').trim() : '';
                const reference = col.reference >= 0 ? String(raw[col.reference] ?? '').trim() : '';
                const priceIncludingVAT = col.priceIncludingVAT >= 0
                    ? parseBoolCell(raw[col.priceIncludingVAT], true)
                    : true;
                const bankName = col.bankName >= 0 ? String(raw[col.bankName] ?? '').trim() : '';
                const bankCode = col.bankCode >= 0 ? String(raw[col.bankCode] ?? '').trim() : '';
                const bankBranchCode = col.bankBranchCode >= 0 ? String(raw[col.bankBranchCode] ?? '').trim() : '';
                const supplierAccountNumber = col.supplierAccountNumber >= 0
                    ? String(raw[col.supplierAccountNumber] ?? '').trim()
                    : '';

                const errors = [];
                if (!caseId) {
                    errors.push(t('CaseId'));
                } else if (allowedCaseIds.size > 0 && !allowedCaseIds.has(caseId)) {
                    // The caseId must belong to one of this accountant's clients
                    // (leading business: any sub-client; non-leading: the current
                    // client only). Rows with any other caseId are rejected.
                    errors.push(
                        t('CaseIdNotUnderAccountant')
                        || t('InvalidCaseId')
                        || 'Invalid CaseId'
                    );
                }

                if (type === null || type === undefined) errors.push(t('PaymentType') || 'Type');
                if (!dateDay || !dateDay.isValid()) errors.push(t('Date'));
                if (!Number.isFinite(amount) || amount === 0) errors.push(t('Amount'));
                if (!description) errors.push(t('Description'));

                const typeLabel = {
                    0: t('CreditCard'),
                    1: t('Check'),
                    2: t('BankTransfer'),
                    3: t('Cash'),
                }[type] || '';

                rows.push({
                    __row: i + 2, // spreadsheet row number (1-based + header)
                    caseId,
                    clientName: caseIdToName.get(caseId) || '',
                    type,
                    typeLabel,
                    date: dateDay ? dateDay.toDate() : null,
                    amount: Number.isFinite(amount) ? amount : '',
                    description,
                    reference,
                    priceIncludingVAT,
                    bankName,
                    bankCode,
                    bankBranchCode,
                    supplierAccountNumber,
                    errors,
                });
            });

            setParsedRows(rows);
        } catch (err) {
            console.error('Excel parse failed', err);
            setParseError(t('FailedToReadFile') || 'Failed to read file');
            setParsedRows([]);
        } finally {
            // allow re-selecting the same file
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemoveRow = (idx) => {
        setParsedRows((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleClear = () => {
        setParsedRows([]);
        setFileName('');
        setParseError('');
    };

    const handleImport = async () => {
        if (!onImport || validRows.length === 0) return;

        // Defense-in-depth: even though invalid rows are already filtered out
        // of validRows by the preview validator, re-check that every row's
        // caseId belongs to this accountant before hitting the server.
        const safeRows = validRows.filter((r) => {
            if (allowedCaseIds.size === 0) return true; // no list to check against
            return allowedCaseIds.has(String(r.caseId));
        });

        if (safeRows.length === 0) {
            return;
        }

        // Build payment objects in the shape expected by CollectionPage.handleAddItem
        const payments = safeRows.map((r) => ({
            type: String(r.type),
            date: r.date,
            amount: Number(r.amount),
            description: r.description,
            reference: r.reference,
            bankName: r.bankName || null,
            bankCode: r.bankCode || null,
            bankBranchCode: r.bankBranchCode || null,
            supplierAccountNumber: r.supplierAccountNumber || null,
            priceIncludingVAT: !!r.priceIncludingVAT,
            caseId: r.caseId,
        }));

        const ok = await onImport(payments);
        if (ok) handleClear();
    };

    /* ─── UI ──────────────────────────────────────────────────── */
    return (
        <Box sx={{ width: '100%', border: '1px dashed #c0c7f5', borderRadius: '8px', p: 2, backgroundColor: '#fafbff' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                    {t('ImportFromExcel') || 'ייבוא מ-Excel'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadTemplate}
                        sx={{ borderRadius: '8px', '& .MuiButton-startIcon': { margin: 0 }, gap: 1 }}
                    >
                        {t('DownloadTemplate')}
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{ borderRadius: '8px', '& .MuiButton-startIcon': { margin: 0 }, gap: 1 }}
                    >
                        {t('UploadFile')}
                    </Button>
                    {parsedRows.length > 0 && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<DownloadIcon />}
                            onClick={handleExportCurrent}
                            sx={{ borderRadius: '8px', '& .MuiButton-startIcon': { margin: 0 }, gap: 1 }}
                        >
                            {t('Export')}
                        </Button>
                    )}
                    <input
                        type="file"
                        ref={fileInputRef}
                        hidden
                        accept=".xls,.xlsx,.csv"
                        onChange={handleFileChange}
                    />
                </Box>
            </Box>

            {fileName && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {fileName}
                </Typography>
            )}

            {parseError && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {parseError}
                </Typography>
            )}

            {parsedRows.length > 0 && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                        <Chip size="small" color="primary" label={`${t('Total') || 'סה"כ'}: ${parsedRows.length}`} />
                        <Chip size="small" color="success" label={`${t('Valid') || 'תקינות'}: ${validRows.length}`} />
                        {invalidRowsCount > 0 && (
                            <Chip size="small" color="error" label={`${t('ValidationErrors') || 'שגיאות'}: ${invalidRowsCount}`} />
                        )}
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" color="inherit" onClick={handleClear}>
                            {t('Clear') || 'נקה'}
                        </Button>
                    </Box>

                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>{t('Status') || 'סטטוס'}</TableCell>
                                    <TableCell>{HE_COLUMNS_SHORT.caseId}</TableCell>
                                    <TableCell>{t('Client') || 'לקוח'}</TableCell>
                                    <TableCell>{HE_COLUMNS_SHORT.type}</TableCell>
                                    <TableCell>{HE_COLUMNS_SHORT.date}</TableCell>
                                    <TableCell>{HE_COLUMNS_SHORT.amount}</TableCell>
                                    <TableCell>{HE_COLUMNS_SHORT.description}</TableCell>
                                    <TableCell>{HE_COLUMNS.reference}</TableCell>
                                    <TableCell>{HE_COLUMNS_SHORT.priceIncludingVAT}</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {parsedRows.map((r, i) => {
                                    const hasErr = r.errors.length > 0;
                                    return (
                                        <TableRow
                                            key={i}
                                            hover
                                            sx={{ backgroundColor: hasErr ? '#fff5f5' : 'inherit' }}
                                        >
                                            <TableCell>{r.__row}</TableCell>
                                            <TableCell>
                                                {hasErr ? (
                                                    <Tooltip title={r.errors.join(', ')}>
                                                        <ErrorIcon color="error" fontSize="small" />
                                                    </Tooltip>
                                                ) : (
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                )}
                                            </TableCell>
                                            <TableCell>{r.caseId}</TableCell>
                                            <TableCell>{r.clientName}</TableCell>
                                            <TableCell>{r.typeLabel}</TableCell>
                                            <TableCell>{r.date ? dayjs(r.date).format('DD/MM/YYYY') : ''}</TableCell>
                                            <TableCell>{r.amount}</TableCell>
                                            <TableCell>{r.description}</TableCell>
                                            <TableCell>{r.reference}</TableCell>
                                            <TableCell>{r.priceIncludingVAT ? (t('Yes') || 'כן') : (t('No') || 'לא')}</TableCell>
                                            <TableCell>
                                                <IconButton size="small" onClick={() => handleRemoveRow(i)}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={isImporting ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
                            disabled={validRows.length === 0 || isImporting}
                            onClick={handleImport}
                            sx={{
                                borderRadius: '8px',
                                '& .MuiButton-startIcon': { margin: 0 },
                                gap: 1,
                                backgroundColor: '#304FFF',
                                '&:hover': { backgroundColor: '#1565c0' },
                            }}
                        >
                            {t('Import')} ({validRows.length})
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default PaymentsExcelImport;
